import React from 'react';
import I18n from '@/modules/i18n';
import styles from '@/components/shared/Correlations/Correlations.module.scss';
import * as Routes from '@/modules/routes';
import { UnitType, TagType } from '@/components/shared/Correlations/types';
import useUnitTagValues from '@/components/shared/Correlations/hooks/useUnitTagValues';
import cn from '@/utils/cn';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import { COLORS } from '@/components/ui/ui.constants';
import { outlineStyles } from '@/components/shared/Correlations/TagCard/shared';

type UnitTagContentType = {
  programId: number,
  tag: TagType,
  unit: UnitType,
  headerLabel: string,
  isExpanded: boolean;
  toggleExpanded: (key: string) => void;
};

const UnitTagContent = ({ isExpanded, programId, tag, unit, headerLabel, toggleExpanded }: UnitTagContentType) => {
  const { data: tagValues, isLoading } = useUnitTagValues({
    isEnabled: isExpanded, programId, tagId: tag.id, unitId: unit.id,
  });

  return (
    <div key={headerLabel} className={styles.lesson}>
      <Text asChild size="sm" weight={isExpanded ? 'bold' : 'normal'}>
        <button
          aria-expanded={isExpanded}
          className={cn(
            `tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none
                   hover:tw-bg-white hover:tw-bg-opacity-25 focus:tw-bg-white focus:tw-bg-opacity-25
                   focus:tw-outline-none`,
            outlineStyles,
            styles.tagButton,
          )}
          type="button"
          onClick={() => toggleExpanded(`Unit-${unit.id}`)}
        >
          <Icon
            // A box exactly one line tall, top-aligned to the line box, centers the glyph on the
            // first line. `align-middle` sits it on the baseline plus half the x-height, which
            // left it ~2px low.
            className="tw-mr-[8px] tw-inline-block tw-h-[length:1lh] tw-align-top"
            fill={COLORS['very-dark-grey']}
            icon={isExpanded ? 'ChevronDown' : 'ChevronRight'}
            size={12}
          />
          {headerLabel}
          {isExpanded && isLoading && <i className="fa fa-spinner fa-spin tw-ml-[8px]" />}
        </button>
      </Text>
      {isExpanded && (
        <div className="tw-ml-[20px] tw-mt-[8px]">
          {isLoading && (
            <div>{I18n.t('loading_tag_values')}</div>
          )}

          {!isLoading && !tagValues && (
            <div>{I18n.t('no_content_for_tag')}</div>
          )}

          {!isLoading && tagValues && (
            <ul className="tw-list-disc">
              {tagValues.notebooks.map(notebook => (
                <li key={notebook.id}>
                  <Text asChild size="sm">
                    <a
                      className={outlineStyles}
                      href={Routes.stream_shared_program_investigations_path(
                        programId,
                        { anchor: `section-${notebook.id}`,
                          expanded: notebook.id,
                          unit_id: unit.id,
                          unit_projects: true,
                        },
                      )}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {`${I18n.t('unit_projects')} / ${notebook.label}`}
                    </a>
                  </Text>
                </li>
              ))}
              {tagValues.slideShows?.map(slideShow => (
                <li key={slideShow.id}>
                  <Text asChild size="sm">
                    <a
                      className={outlineStyles}
                      href={slideShow.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {`${I18n.t('unit_slideshows')} / ${slideShow.label}`}
                    </a>
                  </Text>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default UnitTagContent;
